import Script from "next/script";
import { FunctionComponent, PropsWithChildren } from "react";

import { SB_NATION_NETWORK_ID } from "@framework/config/community/communities";

interface Props {
  communityName: string;
  communityId: number;
  deployEnv: string;
  networkId: number;
}

interface AdmiralKey {
  localStorageKey: string;
  admiralUrl: string;
}

const admiralKeys: { [index: string]: AdmiralKey } = {
  Eater: {
    localStorageKey: "_aQS02NDc5MDdGOUVBRjRCODk4QjNGNEM5NjItNw",
    admiralUrl: "https://workhorsefunds.com/j/q5fn_fz5k1ggl.bundle.js",
  },
  "The Verge": {
    localStorageKey: "_aQS02NDc5MDdGOUVBRjRCODk4QjNGNEM5NjItMg",
    admiralUrl: "https://worldrealize.com/chunks/l2aod777_3ir2.v2.js",
  },
};

export const getAdmiralEnvironment = (deployEnv: string) => {
  if (deployEnv === "production") {
    return "production";
  }

  if (deployEnv === "pre-production" || deployEnv === "staging") {
    return "staging";
  }

  return "development";
};

const Admiral: FunctionComponent<PropsWithChildren<Props>> = ({
  communityName,
  communityId,
  deployEnv,
  networkId,
}) => {
  if (networkId === SB_NATION_NETWORK_ID) {
    const environment = getAdmiralEnvironment(deployEnv);

    return (
      <Script
        async
        id="admiral-script"
        src={`https://concertads-configs.vox-cdn.com/admiral/${environment}/${communityId}/bootstrap.js`}
        strategy="beforeInteractive"
      />
    );
  }

  // make sure we have Admiral key value pairs
  // For Eater, sub-community names include Eater (i.e., Eater LA)
  const network =
    Object.keys(admiralKeys).find((network) => {
      return communityName.includes(network);
    }) || communityName;
  const admiralKey = admiralKeys[network];

  if (!admiralKey) {
    return;
  }
  const { localStorageKey, admiralUrl } = admiralKey;
  return (
    <Script
      /*
        https://admrl.notion.site/Install-Tag-API-e7180f6afa7d4f369e861e22d1db348b
        The script below is modified to insert the admiral script below itself in the body
        instead of adding it to the head since all our other third party scripts are in the body.
      */
      id="admiral-script"
      dangerouslySetInnerHTML={{
        __html: `!(function (q, _name) {
          (q[_name] = q[_name] || function k() {
            (k.q = k.q || []).push(arguments);
          }), (q[_name].v = q[_name].v || 2),
          (q[_name].s = "3");
          // Pause targeting to allow late targeting values before candidate call
          q[_name]("targeting", "pause");
          !(function (q, k, F, H) {
            function m(F, H) {
              try {
                (m = q.localStorage),
                (F = JSON.parse(m[decodeURI(decodeURI("%67%25%36%35%2574%25%34%39tem"))]("${localStorageKey}")).lgk || []) &&
                (H = q[k].pubads()) &&
                  F.forEach(function (q) {
                      q && q[0] && H.setTargeting(q[0], q[1] || "");
                  });
              } catch (N) {}
              var m;
            }
            try {
                ((H = q[k] = q[k] || {}).cmd = H.cmd || []),
                typeof H.pubads === F ? m() : typeof H.cmd.unshift === F ? H.cmd.unshift(m) : H.cmd.push(m);
            } catch (N) {}
          })(window, decodeURI(decodeURI("g%25%36f%25%36f%2567l%2565%2574%61%2567")), "function");
        })(window, decodeURI(decodeURI("%2561%25%364m%256%39%2572%25%361%256c")));

        !(function (q, k, F, H) {
          (F = q.createElement(k)),
          (q = q.getElementsByTagName(k)[0]),
          (F.async = 1),
          (F.src = "${admiralUrl}"),
          (H = 0) && H(F),
          q.parentNode.insertBefore(F, q),
          F.onload = function () {
            /**
             * Gets a cookie value by name
             * @param {string} name - Cookie name to retrieve
             * @returns {string|null} Cookie value or null if not found
             */
            const getCookie = (name) => {
              const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
              return match ? match[2] : null;
            };

            /**
             * Checks login status from auth cookies
             * @returns {boolean} True if user is logged in, false otherwise
             */
            const getLoginStatusFromCookies = () => {
              return getCookie('duet:authenticated') === 'true' ||
                     getCookie('duet:identityAuthenticated') === 'true';
            };

            let max_attempts = 3;
            let attempts = 0;
            const pollForConcert = () => {
              if (Object.prototype.hasOwnProperty.call(window, "concertAds")) {
                const concertTargeting = {
                  entry_group: window.concertAds?.variables?.entry_group  || [] ,
                  login: String(getLoginStatusFromCookies()),
                  entitlements: window.concertAds?.variables?.entitlements || []
                };

                if (Object.prototype.hasOwnProperty.call(window, "admiral")) {
                  window.admiral = window.admiral || function () {
                    (admiral.q = admiral.q || []).push(arguments);
                  };
                  setAdmiralTargeting(concertTargeting);
                }
              } else {
                attempts++;
                if (attempts < max_attempts) {
                  setTimeout(pollForConcert, 500);
                } else {
                  /**
                   * ConcertAds not found after max attempts.
                   * Set login from cookies and resume targeting.
                   */
                  const concertTargeting = {
                    entry_group: [],
                    login: String(getLoginStatusFromCookies()),
                    entitlements: []
                  };
                  setAdmiralTargeting(concertTargeting);
                }
              }
            }
            const setAdmiralTargeting = (concertTargeting) => {
              Object.keys(concertTargeting).forEach(key => {
                const value = concertTargeting[key];
                window.admiral("targeting", "set", key, value);
              });
              // Resume targeting after all values are set
              window.admiral("targeting", "ready");
            }
            pollForConcert();
          }
        })(document, "script");`,
      }}
    />
  );
};

export default Admiral;
